import { mdiAlertCircleOutline, mdiAlertOutline, mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import * as S from './banner.styles';
import type { BannerTone } from './banner.styles';

export type { BannerTone };

export interface IBannerProps {
    tone?: BannerTone;
    title: React.ReactNode;
    /** Optional second line under the title. */
    children?: React.ReactNode;
    /** Optional control on the right, e.g. a retry button. */
    action?: React.ReactNode;
    className?: string;
}

const ICONS: Record<BannerTone, string> = {
    info: mdiInformationOutline,
    warning: mdiAlertOutline,
    danger: mdiAlertCircleOutline,
    neutral: mdiInformationOutline,
};

/**
 * An inline notice that stays on the page — not a toast, which disappears before anyone acts on it.
 *
 * `danger` is announced assertively (`role="alert"`); the other tones politely (`role="status"`),
 * so a screen reader is not interrupted by a notice that is merely informative.
 */
export function Banner({ tone = 'info', title, children, action, className }: IBannerProps): React.ReactElement {
    return (
        <S.Banner tone={tone} className={className} role={tone === 'danger' ? 'alert' : 'status'}>
            <S.IconSlot aria-hidden={true}>
                <Icon path={ICONS[tone]} size={0.75} />
            </S.IconSlot>
            <S.Content>
                <S.Title>{title}</S.Title>
                {children && <S.Body>{children}</S.Body>}
            </S.Content>
            {action && <S.Action>{action}</S.Action>}
        </S.Banner>
    );
}
